import axios from "axios";
import {TEMPLATE_URLS,get_request_config} from "@/utils/system_constants/constant";
import {first_non_empty_number,parse_response_data} from "./esigning_setup_helpers";

const template_request_client = axios.create({
    baseURL: TEMPLATE_URLS
});

function get_error_message(response_payload, error) {
    const error_message = response_payload?.message || error?.message || "Failed to load the populated document preview.";
    return error_message;
}

function is_safe_positive_id(value) {
    const is_safe_id = Number.isInteger(value) && value > 0;
    return is_safe_id;
}

function validate_template_document_id(template_document_id) {
    if (is_safe_positive_id(template_document_id)) {
        const validated_template_document_id = template_document_id;
        return validated_template_document_id;
    }
    const invalid_template_error = new Error("Invalid template identifier for populated document preview.");
    throw invalid_template_error;
}

function build_submission_preview_url(template_document_id) {
    const validated_template_document_id = validate_template_document_id(template_document_id);
    const submission_preview_url = `/api/docuseal/templates/${validated_template_document_id}/create-submission-preview/`;
    return submission_preview_url;
}

async function request_document_preview(template_document_id, request_payload, request_config) {
    const preview_url = build_submission_preview_url(template_document_id);
    const response = await template_request_client.post(
        preview_url,
        request_payload,
        request_config
    );
    const parsed_response = parse_response_data(response.data);
    return parsed_response;
}

function get_corrected_template_document_id(error, resolved_template_document_id) {
    const response_payload = parse_response_data(error?.response?.data);
    const corrected_template_document_id = first_non_empty_number(response_payload?.details?.template_id);
    if (!corrected_template_document_id || corrected_template_document_id === resolved_template_document_id) {
        const corrected_template_error = new Error(get_error_message(response_payload, error));
        throw corrected_template_error;
    }
    return corrected_template_document_id;
}

async function retry_document_preview_request(error, resolved_template_document_id, request_payload, request_config) {
    const corrected_template_document_id = get_corrected_template_document_id(
        error,
        resolved_template_document_id
    );
    const retry_request = await request_document_preview(
        corrected_template_document_id,
        request_payload,
        request_config
    );
    return retry_request;
}

export function build_preview_request_payload(application_id, template_entity_type_id) {
    const preview_request_payload = {
        application_id,
        template_entity_type_id
    };
    return preview_request_payload;
}

export function get_resolved_template_document_id(template_entity_type_id, template_document_id) {
    const resolved_template_document_id = template_entity_type_id || template_document_id;
    return resolved_template_document_id;
}

function handle_preview_transport_error(vm, error, build_preview_error_result) {
    if (!(error instanceof Error) || error?.response) {
        const empty_transport_error = null;
        return empty_transport_error;
    }
    const error_message = error.message || "Failed to load the populated document preview.";
    vm.show_error(error_message);
    const transport_error_result = build_preview_error_result(error_message);
    return transport_error_result;
}

function handle_retry_preview_error(vm, retry_error, build_preview_error_result) {
    const error_message = retry_error?.message || "Failed to load the populated document preview.";
    vm.show_error(error_message);
    const retry_error_result = build_preview_error_result(error_message);
    return retry_error_result;
}

function retry_preview_request(vm, error, resolved_template_document_id, request_payload, request_config, build_preview_error_result) {
    return retry_document_preview_request(
        error,
        resolved_template_document_id,
        request_payload,
        request_config
    ).catch((retry_error) => {
        const retry_preview_error_result = handle_retry_preview_error(vm, retry_error, build_preview_error_result);
        return retry_preview_error_result;
    });
}

export async function request_preview_with_retry(
    vm,
    resolved_template_document_id,
    request_payload,
    build_preview_error_result
) {
    const request_config = get_request_config();
    try {
        const preview_request_result = await request_document_preview(
            resolved_template_document_id,
            request_payload,
            request_config
        );
        return preview_request_result;
    } catch (error) {
        const transport_error_result = handle_preview_transport_error(vm, error, build_preview_error_result);
        if (transport_error_result) {
            const handled_transport_error = transport_error_result;
            return handled_transport_error;
        }

        const retried_preview_result = await retry_preview_request(
            vm,
            error,
            resolved_template_document_id,
            request_payload,
            request_config,
            build_preview_error_result
        );
        return retried_preview_result;
    }
}
